/*
 * CAPTURE-OWNED MODULE
 * DocTypeSelector — live document-type picker backed by the canonical registry
 * (iim_document_types). Used both in the Template Editor (assign/change a
 * template's document_type, Feature A) and the Template Builder start screen
 * (create-time picker, repointed off the old hardcoded list).
 *
 * Inline "+ New type" creates a tenant type from a NAME ONLY (decisions 9 to
 * 12, decisions/capture/2026-10-17-template-studio-field-model-redesign.md):
 * category General, no department, and one detection phrase, the name itself
 * lowercased. POST /templates/document-types refuses an empty phrase list
 * (the registry loader drops a phrase-less type, so it could never be
 * matched); the name as the one phrase satisfies that guard unchanged. More
 * phrases are added later on the Document Types page.
 *
 * While the name is typed, similar existing types are listed
 * (DocTypeMatchList), active and inactive. The list never blocks, except when
 * the name's key IS an existing type's key: (tenant_id, key) is unique, so
 * that create could only 409.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { captureAPI } from '../../services/api';
import { findDocTypeSuggestions } from '../../utils/docTypeSuggestions';
import DocTypeEditModal from './DocTypeEditModal';
import DocTypeMatchList from './DocTypeMatchList';
import './DocTypeSelector.css';

export default function DocTypeSelector({
  value,
  onChange,
  disabled = false,
  label = 'Document Type',
  className = '',
  showTypeMeta = false,
  // Department/Category behind a "Department & category" disclosure (a small
  // popover) instead of inline — the Studio header uses it when narrow.
  compactMeta = false,
}) {
  const [docTypes, setDocTypes] = useState([]);
  // Every row, inactive included: the similar-type list and the key collision
  // check need them (an inactive type's key still blocks a create).
  const [allTypes, setAllTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Modal edit of the *assigned type's* department/category (showTypeMeta only).
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const metaRef = useRef(null);

  // The compact disclosure closes on Escape or a press outside it.
  useEffect(() => {
    if (!metaOpen) return undefined;
    const onDown = (e) => { if (metaRef.current && !metaRef.current.contains(e.target)) setMetaOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMetaOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [metaOpen]);

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await captureAPI.getDocumentTypes();
      // Registry returns global + tenant rows, active and inactive; the select
      // only shows active ones.
      const rows = res?.documentTypes || [];
      setAllTypes(rows);
      setDocTypes(rows.filter((dt) => dt.is_active));
      setError(null);
    } catch (err) {
      setError('Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, []);

  // Department list only needed when we show/edit the assigned type's metadata.
  useEffect(() => {
    if (!showTypeMeta) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await captureAPI.getDepartments();
        if (!cancelled) setDepartments((res?.departments || []).filter((d) => d.is_active));
      } catch {
        if (!cancelled) setDepartments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [showTypeMeta]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const suggestions = useMemo(
    () => findDocTypeSuggestions({ label: newLabel, docTypes: allTypes }),
    [newLabel, allTypes],
  );
  // The only blocks: nothing typed, a name with no letter or digit (empty key,
  // which the server refuses), and a key that already exists.
  const createBlocked = !newLabel.trim() || suggestions.keyEmpty || !!suggestions.collision;

  const handleCreate = async () => {
    const trimmed = newLabel.trim();
    if (saving || createBlocked) return;
    setSaving(true);
    setError(null);
    try {
      const res = await captureAPI.createDocumentType({
        label: trimmed,
        category: 'General',
        primary_dept_key: null,
        // The name is the one phrase: enough for the server's phrases guard,
        // and the words most likely to be printed on the document.
        detection_phrases: [trimmed.toLowerCase()],
      });
      const created = res?.documentType;
      await loadTypes();
      if (created?.key) onChange(created.key);
      setCreating(false);
      setNewLabel('');
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.details || data?.error || err?.message || 'Failed to create type');
    } finally {
      setSaving(false);
    }
  };

  const cancelCreate = () => {
    setCreating(false);
    setNewLabel('');
    setError(null);
  };

  // "Use existing": select the type for the template, no create call.
  const useExisting = (item) => {
    onChange(item.key);
    cancelCreate();
  };

  // Keep the current value visible even if it isn't an active registry key
  // (e.g. a legacy/system key that was deactivated) so we never blank it out.
  const hasValue = !!value && docTypes.some((dt) => dt.key === value);

  const selectedType = (value && docTypes.find((dt) => dt.key === value)) || null;
  const deptLabel = (key) => departments.find((d) => d.key === key)?.label || key;

  // Department / Category / Edit type — inline, or inside the compact disclosure.
  const renderMetaView = (onEdit) => (
    <div className="dts-meta-view">
      <span className="dts-meta-pair">
        <span className="dts-meta-key">Department:</span>{' '}
        {selectedType.primary_dept_key
          ? <span className="dts-meta-val">{deptLabel(selectedType.primary_dept_key)}</span>
          : <span className="dts-meta-none">—</span>}
      </span>
      <span className="dts-meta-pair">
        <span className="dts-meta-key">Category:</span>{' '}
        <span className="dts-meta-val">{selectedType.category || '—'}</span>
      </span>
      <button
        type="button"
        className="btn btn-sm btn-secondary dts-edit-type-btn"
        onClick={onEdit}
        disabled={disabled}
        title="Edit this document type's department and category"
      >
        Edit type
      </button>
    </div>
  );

  return (
    <div className={`doc-type-selector ${className}`}>
      {label && <label className="dts-label">{label}:</label>}

      {loading ? (
        <span className="dts-loading">Loading…</span>
      ) : creating ? (
        <div className="dts-create-row">
          <input
            className="dts-create-input"
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') cancelCreate();
            }}
            placeholder="New type name (e.g. HR Onboarding)"
            aria-label="New document type name"
            autoFocus
            disabled={saving}
            maxLength={100}
          />
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleCreate}
            disabled={saving || createBlocked}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={cancelCreate}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="dts-select-row">
          <select
            className="dts-select"
            aria-label={label || 'Document type'}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            {!value && <option value="" disabled>Select a type…</option>}
            {!hasValue && value && (
              <option value={value}>{value} (inactive)</option>
            )}
            {docTypes.map((dt) => (
              <option key={dt.key} value={dt.key}>{dt.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-secondary dts-new-btn"
            onClick={() => { setCreating(true); setError(null); }}
            disabled={disabled}
            title="Create a new document type"
          >
            ＋ New type
          </button>
        </div>
      )}

      {error && <span className="dts-error">{error}</span>}
      {creating && (
        <DocTypeMatchList suggestions={suggestions} onUseExisting={useExisting} />
      )}
      {creating && !error && (
        <span className="dts-hint">
          The name is used as its first detection phrase. More phrases can be
          added later in Document Types.
        </span>
      )}

      {showTypeMeta && !creating && selectedType && !compactMeta && (
        <div className="dts-type-meta">
          {renderMetaView(() => setEditingMeta(true))}
        </div>
      )}

      {showTypeMeta && !creating && selectedType && compactMeta && (
        <div className="dts-type-meta dts-type-meta--compact" ref={metaRef}>
          <button
            type="button"
            className="dts-meta-toggle"
            aria-expanded={metaOpen}
            aria-controls="dts-meta-popover"
            onClick={() => setMetaOpen(v => !v)}
          >
            Department &amp; category <span aria-hidden="true">{metaOpen ? '▾' : '▸'}</span>
          </button>
          {metaOpen && (
            <div className="dts-meta-popover" id="dts-meta-popover" role="group" aria-label="Department and category">
              {renderMetaView(() => { setMetaOpen(false); setEditingMeta(true); })}
            </div>
          )}
        </div>
      )}

      {showTypeMeta && editingMeta && selectedType && (
        <DocTypeEditModal
          docType={selectedType}
          departments={departments}
          fields={['department', 'category', 'visibility']}
          note={<>Edits the <strong>“{selectedType.label}”</strong> document type — affects
            all documents of this type, not just this template. (The template’s
            assignment is changed with the selector above.)</>}
          onClose={() => setEditingMeta(false)}
          onSaved={() => loadTypes()}
        />
      )}
    </div>
  );
}
